import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Mic2, CalendarRange, Users, Library,
  TrendingUp, Plus, ArrowRight, Activity
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SocialHealthWidget from "@/components/SocialHealthWidget";
import ProviderHealthWidget from "@/components/ProviderHealthWidget";
import CreditsBurnRateWidget from "@/components/CreditsBurnRateWidget";
import TrialWidget from "@/components/TrialWidget";
import TrialExpiryBanner from "@/components/TrialExpiryBanner";
import AnnualUpgradeBanner from "@/components/AnnualUpgradeBanner";
import CreditsWidget from "@/components/CreditsWidget";
import StudioFeatureBanner from "@/components/StudioFeatureBanner";
import ProductionHeartbeat from "@/components/ProductionHeartbeat";
import PromptLibraryWidget from "@/components/PromptLibraryWidget";
import AnchorTeaserCard from "@/components/AnchorTeaserCard";
import UsageBanner from "@/components/UsageBanner";
import SetupChecklist from "@/components/SetupChecklist";
import RecommendedNextStep from "@/components/RecommendedNextStep";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

import { Clock, TrendingUp as RevenueTrend, ArrowUpRight, CheckCircle2, Volume2, Film, Share2, Newspaper, Send, Disc3, Mic, Megaphone, FileText, Rss, Wand2, Music, Video, Sparkles as SparklesIcon, Languages, Globe2, BarChart3, FileVideo, Radio } from "lucide-react";

function MyriadBus() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/news-studio/myriad-bus")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!data) return null;
  const configured = !!data.connection?.configured;
  const auto = data.auto_mode || {};
  const next = data.next_scheduled;
  const recent = data.recent_pushes || [];
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="myriad-bus-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-emerald-300" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">// MYRIAD BUS</span>
        </div>
        <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded ${
          configured ? "text-emerald-300 bg-emerald-500/15" : "text-ink-muted bg-bg-elevated"
        }`} data-testid="myriad-bus-status">
          {configured ? "● Connected" : "○ Not connected"}
        </span>
      </div>

      {!configured ? (
        <div className="text-xs text-ink-muted py-2">
          Connect Broadcast Radio (Myriad) under <Link to="/dashboard/integrations" className="text-cyan-300 hover:text-cyan-100">Integrations</Link> to wire News Studio bulletins straight into your cart wall.
        </div>
      ) : (
        <>
          {/* Auto-mode badge */}
          <div className="flex items-center gap-2 text-[11px] mb-3" data-testid="myriad-bus-automode">
            <div className={`flex-1 rounded-md border px-2.5 py-1.5 ${
              auto.enabled && auto.auto_push ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-line bg-bg-elevated text-ink-muted"
            }`}>
              {auto.enabled && auto.auto_push
                ? <>Auto-pushing <span className="font-mono text-[10px]">every {auto.interval_minutes} min</span></>
                : <>Auto-push <span className="opacity-70">off</span> — manual only</>}
            </div>
            {data.queued_count > 0 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 px-2.5 py-1.5">
                {data.queued_count} queued
              </div>
            )}
          </div>

          {/* Next scheduled airtime */}
          {next && (
            <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 px-3 py-2 mb-3" data-testid="myriad-bus-next">
              <div className="font-mono text-[9px] uppercase tracking-wider text-cyan-300/80 mb-0.5">Next on air</div>
              <div className="text-sm text-ink-primary truncate">{next.title}</div>
              <div className="font-mono text-[10px] text-cyan-200">
                {new Date(next.scheduled_for).toLocaleString(undefined, {
                  weekday: "short", hour: "2-digit", minute: "2-digit", month: "short", day: "numeric",
                })}
              </div>
            </div>
          )}

          {/* Recent pushes */}
          <div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1.5">Recent pushes</div>
            {recent.length === 0 ? (
              <div className="text-[11px] text-ink-muted py-2">No carts pushed yet. Render a bulletin and click <span className="text-emerald-300">Push to Myriad</span>.</div>
            ) : (
              <div className="space-y-1">
                {recent.map((r, i) => (
                  <div key={i} data-testid="myriad-bus-row"
                       className="flex items-center justify-between gap-2 rounded-md border border-line/50 bg-bg-elevated px-2.5 py-1.5 text-[11px]">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Disc3 className={`h-3 w-3 shrink-0 ${r.source === "auto" ? "text-emerald-300" : "text-cyan-300"}`} />
                      <span className="truncate text-ink-primary">
                        {r.artist || r.title || "—"}
                        {r.artist && r.title && <span className="text-ink-muted"> — {r.title}</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 text-ink-muted">
                      {r.cart_id && <span className="font-mono text-[9px] text-emerald-200">#{r.cart_id}</span>}
                      <span className="font-mono text-[9px]">{new Date(r.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function NewsroomInsights() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/news/analytics?days=7")
      .then((r) => setD(r.data))
      .catch(() => setD(null));
  }, []);
  if (!d) return null;
  const t = d.totals || {};
  const videoPosts = (d.posts_by_platform || []).reduce((acc, p) => acc + (p.count || 0), 0);
  return (
    <Link
      to="/dashboard/news"
      data-testid="newsroom-insights"
      className="group block rounded-2xl border p-5 mb-8 transition hover:scale-[1.005]"
      style={{
        background: "linear-gradient(135deg, rgba(139,92,246,0.10), rgba(245,158,11,0.06))",
        borderColor: "rgba(245,158,11,0.25)",
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber/80 flex items-center gap-1.5">
            <Newspaper className="h-3 w-3" /> // AI NEWSROOM · LAST 7 DAYS
          </div>
          <div className="font-display text-lg font-bold mt-1">
            {(t.in_window || 0)} stories ingested · {t.auto_approved || 0} auto-approved
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-ink-muted group-hover:text-cyan-300 transition" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InsightTile icon={Volume2} label="Anchor mixes" value={t.anchor_mixes || 0}
                     accent="#10B981" sub="air-ready MP3s" />
        <InsightTile icon={Film}    label="Videos rendered" value={t.videos || 0}
                     accent="#8B5CF6" sub="branded MP4s" />
        <InsightTile icon={Share2}  label="Auto-posts queued" value={videoPosts}
                     accent="#0078D4" sub={`across ${(d.posts_by_platform || []).length} channels`} />
        <InsightTile icon={Sparkles} label="Trust avg" value={`${d.trust?.avg_score || 0}/100`}
                     accent="#0093FF" sub="factuality score" />
      </div>
    </Link>
  );
}

function InsightTile({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="rounded-lg border border-line bg-bg-surface/60 p-3" style={{ boxShadow: `inset 0 0 0 1px ${accent}22` }}>
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
        <Icon className="h-3 w-3" style={{ color: accent }} /> {label}
      </div>
      <div className="font-display text-2xl font-black mt-1" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] text-ink-muted mt-0.5">{sub}</div>
    </div>
  );
}

function ApprovalTally() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data: d } = await api.get("/notifications/sponsor-approvals");
        if (mounted) setData(d);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (!data || (data.today_approved === 0 && data.today_rejected === 0)) return null;

  return (
    <Link
      to="/dashboard/commercials"
      className="inline-flex items-center gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 transition px-3 py-2"
      data-testid="approval-tally"
    >
      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
      <div className="leading-tight">
        <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-emerald-400/80">// CLEARED TODAY</div>
        <div className="font-display text-sm font-bold text-ink-primary">
          {data.today_approved} {data.today_approved === 1 ? "spot" : "spots"}
          {data.today_rejected > 0 && (
            <span className="text-rose-400 ml-2 font-normal text-xs">· {data.today_rejected} need changes</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function UpgradeInterestCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/upgrade-interest/summary?days=7").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || data.total_clicks === 0) return null;
  const max = Math.max(...data.features.map((f) => f.clicks));
  return (
    <div className="mb-8 rounded-xl border border-line bg-bg-surface p-5" data-testid="upgrade-interest-card">
      <div className="flex items-center justify-between mb-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// upgrade interest · last {data.days} days</div>
        <span className="font-mono text-[10px] text-brand-amber">{data.total_clicks} locked-feature clicks</span>
      </div>
      <div className="text-xs text-ink-secondary mb-4">Which PRO features free users are trying to open — your hottest upgrade triggers.</div>
      <div className="space-y-2">
        {data.features.map((f) => (
          <div key={f.feature} className="flex items-center gap-3" data-testid={`upgrade-interest-row-${f.feature}`}>
            <div className="w-44 shrink-0 text-sm font-semibold truncate">{f.label}</div>
            <div className="flex-1 h-2 rounded-full bg-bg-elevated overflow-hidden">
              <div className="h-full rounded-full bg-brand-amber/70" style={{ width: `${(f.clicks / max) * 100}%` }} />
            </div>
            <div className="w-28 shrink-0 text-right font-mono text-[11px] text-ink-secondary">
              {f.clicks} clicks · {f.unique_users} user{f.unique_users === 1 ? "" : "s"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueFeed() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.get("/billing/revenue-feed");
        if (mounted) setData(res.data);
      } catch { /* silent — endpoint may be empty before any payments */ }
    };
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (!data) return null;
  const isEmpty = data.today.count === 0 && data.recent.length === 0 && !data.top_gateway;

  return (
    <div className="mb-8 rounded-xl border border-line bg-bg-surface p-5" data-testid="revenue-feed">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <RevenueTrend className="h-4 w-4 text-emerald-400" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">// REVENUE · LIVE</div>
        </div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">
          {data.scope === "platform" ? "Platform-wide" : "Your account"}
        </div>
      </div>

      {isEmpty ? (
        <div className="text-sm text-ink-secondary leading-relaxed" data-testid="revenue-feed-empty">
          No paid subscriptions yet. Configure a payment gateway in{" "}
          <Link to="/dashboard/billing?tab=plan" className="text-brand-amber underline-offset-4 hover:underline">Billing</Link>{" "}
          to start tracking MRR across Stripe, PayPal, Razorpay, Paddle, Creem &amp; Lemon Squeezy.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <RevStat label="Today" value={`$${data.today.total.toFixed(2)}`} sub={`${data.today.count} payments`} testid="rev-today" />
            <RevStat label="Month to date" value={`$${data.mtd.total.toFixed(2)}`} sub="MRR proxy" testid="rev-mtd" />
            <RevStat
              label="Top gateway"
              value={data.top_gateway?.label || "—"}
              sub={data.top_gateway ? `${data.top_gateway.count} · $${data.top_gateway.amount.toFixed(2)}` : "no activity"}
              testid="rev-top"
            />
            <RevStat
              label="Gateways live"
              value={`${data.breakdown.length}`}
              sub={data.breakdown.map((g) => g.label.split(" ")[0]).slice(0, 3).join(" · ") || "—"}
              testid="rev-gateways"
            />
          </div>

          {data.recent.length > 0 && (
            <div className="space-y-1.5" data-testid="rev-recent">
              <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">Recent payments</div>
              {data.recent.map((r, i) => (
                <div key={i} className="flex items-center justify-between rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <ArrowUpRight className="h-3 w-3 text-emerald-400 shrink-0" />
                    <span className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">{r.provider}</span>
                    <span className="text-ink-secondary truncate">{r.plan_id?.toUpperCase()}</span>
                    {r.email && <span className="text-ink-muted text-[10px] truncate hidden sm:inline">· {r.email}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono tabular-nums text-ink-primary">${Number(r.amount || 0).toFixed(2)}</span>
                    <span className="font-mono text-[10px] text-ink-muted uppercase">{(r.currency || "usd").toUpperCase()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RevStat({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-line bg-bg-elevated p-3" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-1 font-display text-lg font-bold text-ink-primary truncate" title={value}>{value}</div>
      <div className="text-[10px] text-ink-muted truncate">{sub}</div>
    </div>
  );
}

function BroadcastClock({ timezone }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const tz = timezone && timezone.trim() ? timezone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  let timeStr = "--:--:--";
  let dateStr = "";
  let tzAbbr = "";
  try {
    timeStr = new Intl.DateTimeFormat("en-GB", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: tz }).format(now);
    dateStr = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "2-digit", timeZone: tz }).format(now);
    const parts = new Intl.DateTimeFormat("en-US", { timeZoneName: "short", timeZone: tz }).formatToParts(now);
    tzAbbr = parts.find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    timeStr = now.toLocaleTimeString("en-GB", { hour12: false });
    dateStr = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit" });
    tzAbbr = tz;
  }
  return (
    <div className="flex items-center gap-3 rounded-md border border-brand-amber/40 bg-brand-amber/5 px-3 py-2" data-testid="broadcast-clock">
      <Clock className="h-4 w-4 text-brand-amber" aria-hidden="true" />
      <div className="leading-tight">
        <div className="font-mono text-[15px] sm:text-base font-semibold tabular-nums tracking-wider text-brand-amber" data-testid="broadcast-clock-time">
          {timeStr}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-muted" data-testid="broadcast-clock-meta">
          {dateStr} · {tzAbbr}
        </div>
      </div>
    </div>
  );
}

const QUICK_ACTIONS = [
  { to: "/dashboard/generate", title: "Generate Caption", icon: Sparkles, hint: "AI Studio" },
  { to: "/dashboard/podcasts", title: "Package Podcast", icon: Mic2, hint: "Podcast Vault" },
  { to: "/dashboard/calendar", title: "Schedule Post", icon: CalendarRange, hint: "Calendar" },
  { to: "/dashboard/team", title: "Invite Crew", icon: Users, hint: "Team" },
];

const NEWSROOM_SHORTCUTS = [
  { to: "/dashboard/creative-studio", title: "Creative Studio", icon: Wand2, hint: "Nano Banana 2 image gen", accent: "#FF8A1F" },
  { to: "/dashboard/news-studio", title: "News Studio", icon: Mic, hint: "AI bulletin engine", accent: "#10B981" },
  { to: "/dashboard/commercials", title: "Commercials", icon: Megaphone, hint: "Spot creator + clearance", accent: "#FF8A1F" },
  { to: "/dashboard/blog-connector", title: "Blog Connector", icon: FileText, hint: "Auto-publish to WordPress", accent: "#0093FF" },
  { to: "/dashboard/rss-feeds", title: "RSS Feeds", icon: Rss, hint: "Ingest sources & syndicate", accent: "#A855F7" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const t = useT();
  const [station, setStation] = useState(null);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [showStationModal, setShowStationModal] = useState(false);
  const [form, setForm] = useState({ name: "", tagline: "", genre: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, a, c] = await Promise.all([
        api.get("/stations/me"),
        api.get("/analytics/overview"),
        api.get("/content?limit=5"),
      ]);
      setStation(s.data);
      setStats(a.data);
      setRecent(c.data);
      if (!s.data) setShowStationModal(true);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const onSwitch = () => load();
    window.addEventListener("station-switched", onSwitch);
    return () => window.removeEventListener("station-switched", onSwitch);
  }, []);

  const createStation = async () => {
    if (!form.name.trim()) { toast.error("Station name required"); return; }
    setSaving(true);
    try {
      await api.post("/stations", form);
      toast.success("Station launched on-air");
      setShowStationModal(false);
      await load();
    } catch {
      toast.error("Failed to create station");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="dashboard-page">
      <PageHeader
        eyebrow={`// ${new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }).toUpperCase()}`}
        title={`${t("Welcome back")}, ${user?.name?.split(" ")[0] || t("broadcaster")}.`}
        description={station ? `${station.name} · ${t(station.genre || "Radio")} ${station.tagline ? "· " + station.tagline : ""}` : t("Let's get your station on the air.")}
        testid="dashboard-header"
      />

      {/* Trial-expiry hero — fires only when ≤3 days remain (Update #116) */}
      <TrialExpiryBanner />
      <AnnualUpgradeBanner />
      <RecommendedNextStep />
      <SetupChecklist />

      {/* Credits usage + Buy-credits modal (Update #117) */}
      <CreditsWidget />

      {/* On-air status strip */}
      {station && (
        <div className="mb-8 rounded-xl border border-line bg-bg-surface p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="onair-strip">
          <div className="flex items-center gap-4 flex-1 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-brand-onair onair-dot" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-onair">{t("On Air")}</span>
            </div>
            <div className="text-sm text-ink-secondary">{station.name}</div>
            <StreamStatusPill url={station.shoutcast_url || station.icecast_url || ""} />
          </div>
          <div className="flex items-center gap-4">
            <ApprovalTally />
            <BroadcastClock timezone={station.timezone} />
            <div className="hidden md:flex items-end gap-0.5 h-6">
              {[0.5, 0.8, 0.3, 0.9, 0.6, 1, 0.4, 0.7, 0.5, 0.8, 0.3, 0.9].map((h, i) => (
                <div key={i} className="w-1 bg-brand-amber/70 wave-bar" style={{ height: `${h * 100}%`, animationDelay: `${i * 0.08}s` }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Production Heartbeat — live activity ticker (last 24h across every studio) */}
      <ProductionHeartbeat />

      {/* Featured studios — high-visibility entry points so AI tools aren't buried in sub-menus */}
      <div className="mb-8 grid gap-4 grid-cols-1 xl:grid-cols-2">
        {/* AI Music Studio — top-priority entry */}
        <StudioFeatureBanner
          to="/dashboard/music-studio"
          testid="dashboard-music-studio-banner"
          icon={Music}
          eyebrow="// AI MUSIC STUDIO"
          title="Produce station-grade music in seconds."
          description="16 broadcaster templates · AI Prompt Builder · BYOK Suno. Auto-trims jingles, sends to Imaging, Podcast, Commercials & Vision."
          capabilities={[
            { icon: SparklesIcon, label: "AI Prompt Builder" },
            { icon: Disc3, label: "16 templates" },
            { icon: Mic2, label: "Podcast intros" },
            { icon: Megaphone, label: "Commercial beds" },
            { icon: Video, label: "Vision soundtracks" },
          ]}
          countEndpoint="/music-studio/tracks"
          countLabel="track"
          countPlural="tracks"
          ctaLabel="Open AI Music Studio"
        />

        {/* AI Avatar Studio */}
        <StudioFeatureBanner
          to="/dashboard/avatar-studio"
          testid="dashboard-avatar-studio-banner"
          icon={Mic2}
          eyebrow="// AI AVATAR STUDIO"
          title="On-camera presenters without the studio."
          description="Synthesia-powered AI avatars read your scripts in 60+ languages — perfect for news bulletins, sponsor reads, social shorts & in-show segments."
          capabilities={[
            { icon: Languages, label: "60+ languages" },
            { icon: Mic, label: "BYOK Synthesia" },
            { icon: FileVideo, label: "MP4 + caption export" },
            { icon: Share2, label: "Auto-post to Socials" },
          ]}
          countEndpoint="/avatar-studio/videos"
          countLabel="avatar video"
          countPlural="avatar videos"
          ctaLabel="Open AI Avatar Studio"
        />

        {/* AI News Studio */}
        <StudioFeatureBanner
          to="/dashboard/news-studio"
          testid="dashboard-news-studio-banner"
          icon={Newspaper}
          eyebrow="// AI NEWS STUDIO"
          title="Newsroom in a box — ingest, write, voice, broadcast."
          description="Pull from RSS, AI-trust-score each story, voice it through 8 anchor styles over a ducked bed, render a vertical social MP4 — all in one click."
          capabilities={[
            { icon: Rss, label: "15 RSS sources" },
            { icon: SparklesIcon, label: "Trust scoring" },
            { icon: Mic, label: "8 anchor voices" },
            { icon: Video, label: "Branded video" },
            { icon: BarChart3, label: "Funnel analytics" },
          ]}
          countEndpoint="/news/stories?limit=200"
          countLabel="story"
          countPlural="stories"
          ctaLabel="Open AI News Studio"
        />

        {/* AI DJ Assistant — fills the 4th tile slot so the grid stays balanced */}
        <StudioFeatureBanner
          to="/dashboard/ai-dj"
          testid="dashboard-ai-dj-banner"
          icon={Radio}
          eyebrow="// AI DJ ASSISTANT"
          title="An always-on co-host for your live broadcast."
          description="Now-playing-aware AI generates the link, intro, weather hand-off & sponsor read — synced to whatever's spinning on your stream right now."
          capabilities={[
            { icon: Mic, label: "Live now-playing" },
            { icon: SparklesIcon, label: "Auto-link generator" },
            { icon: Volume2, label: "Voice + bed-ducking" },
            { icon: Globe2, label: "10 languages" },
          ]}
          ctaLabel="Open AI DJ Assistant"
        />
      </div>

      {/* Live revenue feed */}
      <RevenueFeed />
      <UpgradeInterestCard />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="kpi-grid">
        <KPI label="AI generations" value={stats?.total_content ?? 0} icon={Sparkles} accent />
        <KPI label="Podcast episodes" value={stats?.total_podcasts ?? 0} icon={Mic2} />
        <KPI label="Scheduled posts" value={stats?.total_scheduled ?? 0} icon={CalendarRange} />
        <KPI label="DJs on roster" value={stats?.total_djs ?? 0} icon={Users} />
      </div>

      {/* AI Newsroom this-week insight strip */}
      <NewsroomInsights />

      {/* Grid */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        {/* Quick actions */}
        <div className="rounded-xl border border-line bg-bg-surface p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Quick actions</h3>
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">// SHORTCUTS</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {QUICK_ACTIONS.map((a) => (
              <Link
                key={a.title}
                to={a.to}
                data-testid={`quick-${a.title.toLowerCase().replace(/\s/g, "-")}`}
                className="group flex items-center justify-between rounded-lg border border-line bg-bg-elevated p-4 lift"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-md bg-brand-amber/10 p-2 text-brand-amber">
                    <a.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{a.title}</div>
                    <div className="text-xs text-ink-muted">{a.hint}</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-ink-muted group-hover:text-cyan-300 transition" />
              </Link>
            ))}
          </div>
        </div>

        {/* Station card */}
        <div className="rounded-xl border border-line bg-bg-surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Station</h3>
            <Radio className="h-4 w-4 text-brand-amber" />
          </div>
          {station ? (
            <div>
              <div className="font-display text-2xl font-bold tracking-tight mb-1">{station.name}</div>
              <div className="text-xs text-ink-muted mb-4">{station.genre || "—"}</div>
              <div className="space-y-2 text-xs">
                <Row label="Owner" value={user?.email} />
                <Row label="Role" value={user?.role?.replace("_", " ")} />
                <Row label="Plan" value={user?.subscription_plan ? user.subscription_plan.toUpperCase() : "FREE"} accent />
              </div>
              <Link to="/dashboard/station" className="mt-4 inline-flex items-center gap-1 text-xs text-brand-amber hover:underline" data-testid="dashboard-manage-station">
                Manage station <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          ) : (
            <div>
              <p className="text-sm text-ink-secondary mb-4">Create your first station to start generating.</p>
              <button
                onClick={() => setShowStationModal(true)}
                data-testid="dashboard-create-station"
                className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black amber-glow"
              >
                <Plus className="h-4 w-4" /> Create station
              </button>
            </div>
          )}
        </div>
      </div>

      {/* News-Studio · Myriad Bus widget */}
      <div className="mb-8">
        <MyriadBus />
      </div>

      {/* Newsroom shortcuts — News Studio · Commercials · Blog Connector · RSS Feeds */}
      <div className="mb-8 rounded-xl border border-line bg-bg-surface p-6" data-testid="newsroom-shortcuts">
        <div className="flex items-center justify-between mb-4">
          <h3 className="type-section-heading">Newsroom shortcuts</h3>
          <span className="font-mono type-status-badge uppercase tracking-wider text-ink-muted">// JUMP IN</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {NEWSROOM_SHORTCUTS.map((a) => (
            <Link
              key={a.title}
              to={a.to}
              data-testid={`newsroom-shortcut-${a.title.toLowerCase().replace(/\s/g, "-")}`}
              className="group flex items-center justify-between rounded-lg border border-line bg-bg-elevated p-4 lift"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="rounded-md p-2 shrink-0" style={{ background: `${a.accent}1A`, color: a.accent }}>
                  <a.icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="type-list-title text-ink-primary truncate">{a.title}</div>
                  <div className="type-list-subtitle text-ink-muted truncate">{a.hint}</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-ink-muted group-hover:text-cyan-300 transition shrink-0 ml-2" />
            </Link>
          ))}
        </div>
      </div>

      {/* Social-publishing health + Provider health + Credits burn rate + Prompt Library */}
      <div className="mb-8 grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <SocialHealthWidget />
        <ProviderHealthWidget />
        <CreditsBurnRateWidget />
        <PromptLibraryWidget />
      </div>

      {/* Free-trial status (hidden once user converts to a paid plan) */}
      <div className="mb-8">
        <TrialWidget />
      </div>

      {/* AI Provider BYOK Usage — Update #97 */}
      <div className="mb-8">
        <UsageBanner />
      </div>

      {/* AI Anchor Studio teaser — today's top story rendered as talking head */}
      <div className="mb-8">
        <AnchorTeaserCard />
      </div>

      {/* Recent generations */}
      <div className="rounded-xl border border-line bg-bg-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold">Recent generations</h3>
          <Link to="/dashboard/library" className="text-xs text-brand-amber hover:underline" data-testid="dashboard-view-library">
            View all →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="text-center py-12 text-ink-muted text-sm">
            <Library className="h-8 w-8 mx-auto mb-2 opacity-50" />
            Nothing here yet. Head over to the AI Studio.
          </div>
        ) : (
          <div className="space-y-2">
            {recent.map((c) => (
              <div key={c.content_id} className="rounded-lg border border-line bg-bg-elevated p-4 lift" data-testid={`recent-${c.content_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="rounded-full bg-brand-amber/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-amber">
                    {c.type.replace(/_/g, " ")}
                  </span>
                  {c.platform && (
                    <span className="rounded-full bg-bg-surface px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-secondary border border-line">
                      {c.platform}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-ink-muted">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-ink-secondary line-clamp-2">{c.output}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showStationModal} onOpenChange={setShowStationModal}>
        <DialogContent className="bg-bg-surface border border-line text-ink-primary" data-testid="station-create-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Launch your station</DialogTitle>
            <DialogDescription className="text-ink-secondary">
              Spin up your control room. You can tweak everything later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Station name *</Label>
              <Input
                data-testid="station-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Midnight Frequency FM"
                className="mt-1 bg-bg border border-line"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Genre</Label>
              <Input
                data-testid="station-genre-input"
                value={form.genre}
                onChange={(e) => setForm({ ...form, genre: e.target.value })}
                placeholder="Afro-house, Indie, Talk..."
                className="mt-1 bg-bg border border-line"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Tagline</Label>
              <Input
                data-testid="station-tagline-input"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="The sound of the city after dark."
                className="mt-1 bg-bg border border-line"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Description</Label>
              <Textarea
                data-testid="station-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="A short description for AI context..."
                className="mt-1 bg-bg border border-line"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={createStation}
              disabled={saving}
              data-testid="station-create-submit"
              className="rounded-md bg-brand-amber px-5 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-50"
            >
              {saving ? "Launching..." : "Launch station"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl border bg-bg-surface p-5 lift ${accent ? "border-brand-amber/40" : "border-line"}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-brand-amber" : "text-ink-muted"}`} />
      </div>
      <div className="font-display text-4xl font-black tracking-tighter">{value}</div>
      <div className="mt-2 text-xs flex items-center gap-1 text-ink-muted">
        <Activity className="h-3 w-3" />
        Live count
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{label}</span>
      <span className={`font-mono text-xs ${accent ? "text-brand-amber" : "text-ink-primary"}`}>{value}</span>
    </div>
  );
}

function StreamStatusPill({ url }) {
  const [status, setStatus] = React.useState(null);
  React.useEffect(() => {
    if (!url) { setStatus({ unconfigured: true }); return; }
    let cancelled = false;
    api.get(`/stream/status?url=${encodeURIComponent(url)}`)
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => { if (!cancelled) setStatus({ online: false, error: "ping_failed" }); });
    return () => { cancelled = true; };
  }, [url]);

  if (!status) return (
    <span className="rounded-full border border-line bg-bg-elevated px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-muted" data-testid="stream-pill">
      PINGING STREAM...
    </span>
  );
  if (status.unconfigured) return (
    <span className="rounded-full border border-line bg-bg-elevated px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-muted" data-testid="stream-pill">
      NO STREAM URL
    </span>
  );
  const color = status.online
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
    : "border-brand-onair/40 bg-brand-onair/10 text-brand-onair";
  return (
    <span className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${color}`} data-testid="stream-pill" title={status.icy_name || status.error}>
      STREAM {status.online ? "ONLINE" : "OFFLINE"} · {status.latency_ms}MS
    </span>
  );
}
